import React, { useState, useEffect } from "react";
import { TextField, FormError, Select, Loader } from "components";
import makeRequest from "services/makeRequest";
import { routes } from "routes/apiRoutes";
import {
  isSuccessResponse,
  getAgentId,
  notFoundStatus,
  isObjectEmpty,
  setAlert,
  getRegionOptions,
} from "utils/helper";
import { useSelector } from "react-redux";
import { genderOptions } from "utils/constants";
import ButtonPair from "./ButtonPair";
import { Formik, Form, ErrorMessage } from "formik";
import { agentNOKValidation } from "utils/FormValidations";

const NextOfKin = ({ handleNext, handlePrevious }) => {
  const {
    user,
    regions: { regions },
  } = useSelector((state) => state);

  const agentId = user.user.agent_id || getAgentId();

  const [loading, setLoading] = useState(false);

  const [existingNOKInfo, setExistingNOKInfo] = useState({});

  const [stateOptions, setStateOptions] = useState([]);

  const [agentRegion, setAgentRegion] = useState("");

  const {
    email,
    firstName,
    lastName,
    phoneNumber,
    address,
    region,
    state,
    gender,
    relationship,
  } = existingNOKInfo;

  const initialValues = {
    email: email || "",
    firstName: firstName || "",
    lastName: lastName || "",
    phoneNumber: phoneNumber || "",
    address: address || "",
    region: region || "",
    state: state || "",
    gender: gender || "",
    relationship: relationship || "",
  };

  const getSelectedRegionStates = async (regionName) => {
    try {
      const selectedRegion = regions.find(
        (region) => region.name === regionName
      );
      if (selectedRegion) {
        const { data } = await makeRequest(
          "get",
          routes.fetchRegions(`region=${selectedRegion?.id}&type=1`)
        );
        if (isSuccessResponse(data)) {
          setStateOptions(getRegionOptions(data?.data));
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    const getAgentNOKDetails = async () => {
      setLoading(true);
      try {
        const { data } = await makeRequest(
          "get",
          routes.nextOfKinInfo(agentId)
        );

        if (isSuccessResponse(data)) {
          const agentInfo = {
            ...data?.data,
          };

          setExistingNOKInfo(agentInfo);

          if (agentInfo.region) {
            setAgentRegion(agentInfo.region);
            getSelectedRegionStates(agentInfo.region);
          }
        }
        setLoading(false);
      } catch (error) {
        if (notFoundStatus(error)) {
          setLoading(false);
        }
      }
    };
    getAgentNOKDetails();
    //eslint-disable-next-line
  }, [agentId]);

  useEffect(() => {
    getSelectedRegionStates(agentRegion);
    //eslint-disable-next-line
  }, [agentRegion]);

  const onSubmit = async (values) => {
    try {
      let method = isObjectEmpty(existingNOKInfo) ? "post" : "put";

      const { data } = await makeRequest(
        method,
        routes.nextOfKinInfo(agentId),
        values
      );

      if (isSuccessResponse(data)) {
        handleNext();
      } else {
        setAlert(data.responseMessage);
      }
    } catch (err) {}
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center onboarding-loader">
        <Loader />
      </div>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={agentNOKValidation}
      onSubmit={onSubmit}
    >
      {({
        errors,
        touched,
        isSubmitting,
        values,
        setFieldValue,
        getFieldProps,
      }) => (
        <Form noValidate autoComplete="off">
          <div className="row">
            <div className="col-md-6">
              <TextField
                name="firstName"
                type="text"
                placeholder="Enter First Name"
                label="Next Of Kin's First Name"
                {...getFieldProps("firstName")}
                containerClassName={"mt-3"}
                hasError={errors.firstName && touched.firstName}
              />
              <ErrorMessage component={FormError} name="firstName" />
            </div>
            <div className="col-md-6">
              <TextField
                name="lastName"
                type="text"
                placeholder="Enter Last Name"
                label="Next Of Kin's Last Name"
                {...getFieldProps("lastName")}
                containerClassName={"mt-3"}
                hasError={errors.lastName && touched.lastName}
              />
              <ErrorMessage component={FormError} name="lastName" />
            </div>
            <div className="col-md-6 mt-3">
              <Select
                label="Next of kin's Gender"
                placeholder="Select Gender"
                options={genderOptions}
                name="gender"
                {...getFieldProps("gender")}
                hasError={errors.gender && touched.gender}
              />
              <ErrorMessage component={FormError} name="gender" />
            </div>

            <div className="col-md-6">
              <TextField
                name="email"
                type="text"
                placeholder="Enter email"
                label="Next of kin's Email"
                {...getFieldProps("email")}
                containerClassName={"mt-3"}
                hasError={errors.email && touched.email}
              />
              <ErrorMessage component={FormError} name="email" />
            </div>
            <div className="col-md-6">
              <TextField
                name="phoneNumber"
                label="Next of Kin's Phone Number"
                placeholder="Enter Phone Number"
                type="tel"
                pattern="[0-9]*"
                {...getFieldProps("phoneNumber")}
                containerClassName={"mt-3"}
                hasError={errors.phoneNumber && touched.phoneNumber}
              />
              <ErrorMessage component={FormError} name="phoneNumber" />
            </div>
            <div className="col-md-6">
              <TextField
                name="relationship"
                type="text"
                placeholder="Enter relationship"
                label="Relationship of next of kin with agent"
                {...getFieldProps("relationship")}
                containerClassName={"mt-3"}
                hasError={errors.relationship && touched.relationship}
              />
              <ErrorMessage component={FormError} name="relationship" />
            </div>
            <div className="col-md-6 mt-3">
              <Select
                label="Next of kin's Region"
                placeholder="Select Region"
                options={getRegionOptions(regions)}
                loading={!regions.length}
                name="region"
                onChange={(e) => {
                  let val = e.target.value;
                  setStateOptions([]);
                  setFieldValue("state", "");
                  setFieldValue("region", val);
                  getSelectedRegionStates(val);
                }}
                value={values.region}
                hasError={errors.region && touched.region}
              />
              <ErrorMessage component={FormError} name="region" />
            </div>
            <div className="col-md-6 mt-3">
              <Select
                label="Next of kin's State"
                placeholder="Select state"
                options={stateOptions}
                disabled={!values.region}
                name="state"
                {...getFieldProps("state")}
                hasError={errors.state && touched.state}
              />
              <ErrorMessage component={FormError} name="state" />
            </div>
            <div className="col-md-6">
              <TextField
                name="address"
                type="text"
                placeholder="Enter Address"
                label="Next of kin's Address"
                {...getFieldProps("address")}
                containerClassName={"mt-3"}
                hasError={errors.address && touched.address}
              />
              <ErrorMessage component={FormError} name="address" />
            </div>
          </div>
          <ButtonPair loading={isSubmitting} onPreviousClick={handlePrevious} />
        </Form>
      )}
    </Formik>
  );
};

export default NextOfKin;
